import { useCallback, useEffect, useState } from 'react';

import type { EvaluationRunsService } from '../../services/evaluationRunsService.js';
import type { EvaluationRun } from '../../types/evaluationRun.js';

export interface UseEvaluationRunHistoryParams {
  workspaceName: string;
  resourceName: string;
  runsService: Pick<EvaluationRunsService, 'list' | 'cancel'>;
}

export interface UseEvaluationRunHistoryResult {
  runHistory: EvaluationRun[];
  loading: boolean;
  error: string | null;
  cancellingId: string | null;
  refresh: () => void;
  prependRun: (run: EvaluationRun) => void;
  cancelRun: (runId: string) => void;
}

export function useEvaluationRunHistory({
  workspaceName,
  resourceName,
  runsService,
}: UseEvaluationRunHistoryParams): UseEvaluationRunHistoryResult {
  const [runHistory, setRunHistory] = useState<EvaluationRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (resourceName.length === 0) {
      setRunHistory([]);
      return;
    }
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const runs = await runsService.list(workspaceName, {
          displayNamePrefix: resourceName,
          top: 25,
        });
        setRunHistory(runs.filter((run) => run.displayName === resourceName));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load runs.');
        setRunHistory([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [workspaceName, resourceName, runsService]);

  useEffect(() => {
    setRunHistory([]);
    refresh();
  }, [refresh]);

  const prependRun = useCallback((run: EvaluationRun) => {
    setRunHistory((current) => [run, ...current]);
  }, []);

  const cancelRun = useCallback(
    (runId: string) => {
      void (async () => {
        setError(null);
        setCancellingId(runId);
        try {
          const updated = await runsService.cancel(workspaceName, runId);
          setRunHistory((current) =>
            current.map((run) => (run.id === updated.id ? updated : run)),
          );
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to cancel run.',
          );
        } finally {
          setCancellingId((current) => (current === runId ? null : current));
        }
      })();
    },
    [workspaceName, runsService],
  );

  return {
    runHistory,
    loading,
    error,
    cancellingId,
    refresh,
    prependRun,
    cancelRun,
  };
}
